export type AiAssetContextSource = 'measured' | 'measured-shared' | 'schema-fallback' | 'unavailable';
export type AiAssetContextConfidence = 'high' | 'medium' | 'low';

export type AspectSizePreset = {
  id: string;
  label: string;
  aspectRatio: string;
  imageSize: string;
  openaiSize: 'auto' | '1024x1024' | '1024x1536' | '1536x1024' | '2048x2048';
};

export const ASPECT_SIZE_PRESETS: ReadonlyArray<AspectSizePreset> = [
  { id: 'square_1k', label: 'Square (1:1) · 1K', aspectRatio: '1:1', imageSize: '1K', openaiSize: '1024x1024' },
  { id: 'square_2k', label: 'Square (1:1) · 2K HD', aspectRatio: '1:1', imageSize: '2K', openaiSize: '2048x2048' },
  { id: 'portrait_9_16', label: 'Portrait (9:16) · 1K', aspectRatio: '9:16', imageSize: '1K', openaiSize: '1024x1536' },
  { id: 'portrait_4_5', label: 'Portrait (4:5) · 1K', aspectRatio: '4:5', imageSize: '1K', openaiSize: '1024x1536' },
  { id: 'landscape_16_9', label: 'Landscape (16:9) · 1K', aspectRatio: '16:9', imageSize: '1K', openaiSize: '1536x1024' },
  { id: 'landscape_3_2', label: 'Landscape (3:2) · 1K', aspectRatio: '3:2', imageSize: '1K', openaiSize: '1536x1024' },
  { id: 'cinematic', label: 'Cinematic (21:9)', aspectRatio: '21:9', imageSize: '1K', openaiSize: 'auto' },
  { id: 'auto_max', label: 'Auto / Max quality', aspectRatio: '1:1', imageSize: '4K', openaiSize: 'auto' },
];

export const DEFAULT_PRESET_ID = 'square_1k';

export interface RectContext {
  x: number;
  y: number;
  width: number;
  height: number;
  aspect: number;
  xPercentOfScreen?: number;
  yPercentOfScreen?: number;
  widthPercentOfScreen?: number;
  heightPercentOfScreen?: number;
}

export interface AiAssetScreenContext {
  slotKey: string;
  screenType: string;
  device?: string;
  source: AiAssetContextSource;
  confidence: AiAssetContextConfidence;
  screenRect?: RectContext;
  slotRect?: RectContext;
  imageRect?: RectContext;
  aspectRatio?: {
    value: number;
    label: string;
    orientation: 'portrait' | 'landscape' | 'square';
    nearestPresetId?: string;
    nearestPresetLabel?: string;
  };
  position?: {
    centerXPercent: number;
    centerYPercent: number;
    widthPercentOfScreen: number;
    heightPercentOfScreen: number;
    verticalBand: 'top' | 'middle' | 'bottom' | 'full';
    horizontalBand: 'left' | 'center' | 'right' | 'full';
  };
  rendering?: {
    objectFit?: string;
    objectPosition?: string;
    borderRadiusPx?: number;
    gradientOverlay?: boolean;
    behindTextOverlay?: boolean;
  };
  background?: {
    screenKind: 'solid' | 'photo' | 'clip' | 'none' | 'unknown';
    screenColor?: string;
    luminance?: 'light' | 'dark' | 'mid';
  };
  layout?: {
    screenDescription: string;
    assetRoleDescription: string;
    visualAreaGuidance?: string;
    contentSafeArea?: string;
  };
  warnings?: string[];
}

export type MeasureAiAssetSlotContext = (slotKey: string) => AiAssetScreenContext;

export const SCREEN_CONTEXT_PROMPT_START = '[SCREEN CONTAINER CONTEXT]';
export const SCREEN_CONTEXT_PROMPT_END = '[/SCREEN CONTAINER CONTEXT]';

const MIN_MEASURABLE_SIDE = 8;

const SCREEN_ROOT_TEST_IDS: Record<string, string> = {
  HeroContent: 'view-hero-content',
  Carousel: 'view-carousel',
  ListSinglePick: 'view-list-single-pick',
  ListMultiPick: 'view-list-multi-pick',
  GridSinglePick: 'view-grid-single-pick',
  GridMultiPick: 'view-grid-multi-pick',
  ProgressSteps: 'view-progress-steps',
  HeroList: 'view-hero-list',
  Paywall: 'view-paywall',
  FlexibleLayout: 'view-flexible-layout',
};

function parseRatio(value: string): number | null {
  const [w, h] = value.split(':').map((part) => Number.parseFloat(part));
  if (!Number.isFinite(w) || !Number.isFinite(h) || h <= 0) return null;
  return w / h;
}

export function detectPreset(
  aspectRatio: string | undefined,
  imageSize: string | undefined,
  openaiSize: string | undefined,
  isOpenAI: boolean,
): string {
  for (const preset of ASPECT_SIZE_PRESETS) {
    if (preset.aspectRatio !== (aspectRatio || '1:1')) continue;
    const matches = isOpenAI
      ? preset.openaiSize === (openaiSize || 'auto')
      : preset.imageSize === (imageSize || '1K');
    if (matches) return preset.id;
  }
  return DEFAULT_PRESET_ID;
}

export function getAspectSizePresetById(id: string | undefined): AspectSizePreset | undefined {
  return ASPECT_SIZE_PRESETS.find((preset) => preset.id === id);
}

export function findNearestAspectPreset(aspect: number): AspectSizePreset | undefined {
  if (!Number.isFinite(aspect) || aspect <= 0) return undefined;
  let best: { preset: AspectSizePreset; distance: number } | undefined;
  for (const preset of ASPECT_SIZE_PRESETS) {
    const presetAspect = parseRatio(preset.aspectRatio);
    if (!presetAspect) continue;
    const distance = Math.abs(Math.log(aspect / presetAspect));
    if (!best || distance < best.distance) {
      best = { preset, distance };
    }
  }
  return best?.preset;
}

function round(value: number, decimals = 1): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

function cssEscapeAttribute(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function getScreenType(step: any): string {
  return String(step?.screenType ?? step?.kind ?? 'Unknown');
}

function getRootElement(previewRoot: HTMLElement, screenType: string): HTMLElement | null {
  const testId = SCREEN_ROOT_TEST_IDS[screenType];
  if (testId) {
    const byType = previewRoot.querySelector<HTMLElement>(`[data-testid="${testId}"]`);
    if (byType) return byType;
  }
  return previewRoot.querySelector<HTMLElement>('[data-testid^="view-"][class*="screen-view--edit-mode"]')
    ?? previewRoot.querySelector<HTMLElement>('[data-testid^="view-"]');
}

function isVisibleRect(rect: DOMRect): boolean {
  return rect.width >= MIN_MEASURABLE_SIDE && rect.height >= MIN_MEASURABLE_SIDE;
}

function elementArea(element: HTMLElement): number {
  const rect = element.getBoundingClientRect();
  return rect.width * rect.height;
}

function depthFromRoot(element: HTMLElement, root: HTMLElement): number {
  let depth = 0;
  let current: HTMLElement | null = element;
  while (current && current !== root) {
    depth += 1;
    current = current.parentElement;
  }
  return current === root ? depth : Number.MAX_SAFE_INTEGER;
}

function pickVisibleElement(elements: HTMLElement[], root: HTMLElement): HTMLElement | null {
  const visible = elements.filter((element) => isVisibleRect(element.getBoundingClientRect()));
  if (visible.length === 0) return null;
  return visible.sort((a, b) => {
    const areaDelta = elementArea(b) - elementArea(a);
    if (Math.abs(areaDelta) > 1) return areaDelta;
    return depthFromRoot(a, root) - depthFromRoot(b, root);
  })[0];
}

function closestParentWithMeaningfulRect(element: HTMLElement, root: HTMLElement, maxDepth = 3): HTMLElement {
  let current: HTMLElement | null = element;
  let best = element;
  let depth = 0;

  while (current?.parentElement && current.parentElement !== root.parentElement && depth < maxDepth) {
    const parent: HTMLElement = current.parentElement;
    if (parent === root) break;
    const parentRect = parent.getBoundingClientRect();
    const bestRect = best.getBoundingClientRect();
    if (
      isVisibleRect(parentRect)
      && parentRect.width >= bestRect.width
      && parentRect.height >= bestRect.height
      && parentRect.width <= bestRect.width * 3
      && parentRect.height <= bestRect.height * 3
    ) {
      best = parent;
    }
    current = parent;
    depth += 1;
  }

  return best;
}

function getElementsByEditPath(root: HTMLElement, editPath: string): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(
    `[data-edit-path="${cssEscapeAttribute(editPath)}"]`,
  ));
}

function queryFirstByEditPaths(root: HTMLElement, editPaths: string[]): HTMLElement | null {
  for (const editPath of editPaths) {
    const element = root.querySelector<HTMLElement>(`[data-edit-path="${cssEscapeAttribute(editPath)}"]`);
    if (element) return element;
  }
  return null;
}

function getElementsByEditPathPrefix(root: HTMLElement, prefixes: string[]): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-edit-path]')).filter((element) => {
    const editPath = element.getAttribute('data-edit-path') ?? '';
    return prefixes.some((prefix) => editPath.startsWith(prefix));
  });
}

function getCarouselTarget(root: HTMLElement, slotKey: string): { element: HTMLElement | null; source: AiAssetContextSource; warnings: string[] } {
  const index = Number.parseInt(slotKey.replace('carousel_', ''), 10);
  const requestedSlide = queryFirstByEditPaths(root, [
    `content.carousel.items.${index}`,
    `content.carousel.items[${index}]`,
  ]);
  const activeSlide = pickVisibleElement(
    getElementsByEditPathPrefix(root, ['content.carousel.items.', 'content.carousel.items[']),
    root,
  );
  const slide = requestedSlide && isVisibleRect(requestedSlide.getBoundingClientRect())
    ? requestedSlide
    : activeSlide;
  if (!slide) {
    return { element: null, source: 'unavailable', warnings: ['Carousel slide container is not rendered.'] };
  }

  const imageElement = slide.querySelector<HTMLElement>('[data-edit-path$=".content.image"]');
  const target = imageElement?.parentElement ?? slide;
  const isShared = requestedSlide !== slide;
  return {
    element: target,
    source: isShared ? 'measured-shared' : 'measured',
    warnings: isShared ? ['Requested carousel slide is hidden; using active slide media container geometry.'] : [],
  };
}

function getTargetElement(
  root: HTMLElement,
  screenType: string,
  slotKey: string,
): { element: HTMLElement | null; source: AiAssetContextSource; warnings: string[] } {
  if (slotKey.startsWith('carousel_')) {
    return getCarouselTarget(root, slotKey);
  }

  if (slotKey.startsWith('stepper_')) {
    const mediaContainer = root.querySelector<HTMLElement>('[data-testid="view-progress-steps-media-container"]');
    if (mediaContainer && isVisibleRect(mediaContainer.getBoundingClientRect())) {
      return {
        element: mediaContainer,
        source: 'measured-shared',
        warnings: ['ProgressSteps renders one active segment; using the shared media container geometry.'],
      };
    }
    const imagePathMatch = slotKey.match(/^stepper_(\d+)$/);
    const index = imagePathMatch?.[1];
    const image = index ? queryFirstByEditPaths(root, [
      `content.stepper.items.${index}.content.image`,
      `content.stepper.items[${index}].content.image`,
    ]) : null;
    return {
      element: image,
      source: image ? 'measured' : 'unavailable',
      warnings: image ? ['ProgressSteps media container was unavailable; using rendered image geometry.'] : ['ProgressSteps media container is not rendered.'],
    };
  }

  if (screenType === 'Paywall' && slotKey === 'hero') {
    const mediaElements = getElementsByEditPath(root, 'content.featuredMedia');
    const media = pickVisibleElement(mediaElements, root);
    if (!media) {
      return { element: null, source: 'unavailable', warnings: ['Paywall main media is not rendered.'] };
    }
    return {
      element: closestParentWithMeaningfulRect(media, root, 2),
      source: 'measured',
      warnings: ['Paywall hero slot maps to content.featuredMedia main media.'],
    };
  }

  if (slotKey === 'hero') {
    const hero = pickVisibleElement(getElementsByEditPath(root, 'content.hero'), root);
    if (!hero) {
      return { element: null, source: 'unavailable', warnings: ['Hero media is not rendered.'] };
    }
    const target = screenType === 'HeroList'
      ? closestParentWithMeaningfulRect(hero, root, 2)
      : hero;
    return { element: target, source: 'measured', warnings: [] };
  }

  if (slotKey === 'featured') {
    const featured = pickVisibleElement(getElementsByEditPath(root, 'content.featuredMedia'), root);
    return {
      element: featured,
      source: featured ? 'measured' : 'unavailable',
      warnings: featured ? [] : ['Featured media is not rendered.'],
    };
  }

  return {
    element: null,
    source: 'unavailable',
    warnings: [`Slot ${slotKey} has no stable rendered media context in this iteration.`],
  };
}

function toRectContext(rect: DOMRect, screenRect?: DOMRect): RectContext {
  const base: RectContext = {
    x: round(rect.x),
    y: round(rect.y),
    width: round(rect.width),
    height: round(rect.height),
    aspect: round(rect.width / rect.height, 3),
  };
  if (screenRect && screenRect.width > 0 && screenRect.height > 0) {
    base.xPercentOfScreen = round(((rect.x - screenRect.x) / screenRect.width) * 100);
    base.yPercentOfScreen = round(((rect.y - screenRect.y) / screenRect.height) * 100);
    base.widthPercentOfScreen = round((rect.width / screenRect.width) * 100);
    base.heightPercentOfScreen = round((rect.height / screenRect.height) * 100);
  }
  return base;
}

function orientationForAspect(aspect: number): 'portrait' | 'landscape' | 'square' {
  if (aspect > 1.12) return 'landscape';
  if (aspect < 0.88) return 'portrait';
  return 'square';
}

function ratioLabel(aspect: number): string {
  const nearest = findNearestAspectPreset(aspect);
  if (nearest) return nearest.aspectRatio;
  return `${round(aspect, 2)}:1`;
}

function bandFromCenter(percent: number): 'top' | 'middle' | 'bottom' {
  if (percent < 33) return 'top';
  if (percent > 67) return 'bottom';
  return 'middle';
}

function horizontalBandFromCenter(percent: number): 'left' | 'center' | 'right' {
  if (percent < 33) return 'left';
  if (percent > 67) return 'right';
  return 'center';
}

function getPosition(slotRect: DOMRect, screenRect: DOMRect): AiAssetScreenContext['position'] {
  const centerXPercent = ((slotRect.x + slotRect.width / 2 - screenRect.x) / screenRect.width) * 100;
  const centerYPercent = ((slotRect.y + slotRect.height / 2 - screenRect.y) / screenRect.height) * 100;
  const widthPercentOfScreen = (slotRect.width / screenRect.width) * 100;
  const heightPercentOfScreen = (slotRect.height / screenRect.height) * 100;
  return {
    centerXPercent: round(centerXPercent),
    centerYPercent: round(centerYPercent),
    widthPercentOfScreen: round(widthPercentOfScreen),
    heightPercentOfScreen: round(heightPercentOfScreen),
    verticalBand: heightPercentOfScreen > 80 ? 'full' : bandFromCenter(centerYPercent),
    horizontalBand: widthPercentOfScreen > 80 ? 'full' : horizontalBandFromCenter(centerXPercent),
  };
}

function getLuminance(hexOrCss: string | undefined): 'light' | 'dark' | 'mid' | undefined {
  if (!hexOrCss) return undefined;
  const hex = hexOrCss.trim();
  const match = hex.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return undefined;
  const raw = match[1].length === 3
    ? match[1].split('').map((c) => `${c}${c}`).join('')
    : match[1];
  const r = Number.parseInt(raw.slice(0, 2), 16) / 255;
  const g = Number.parseInt(raw.slice(2, 4), 16) / 255;
  const b = Number.parseInt(raw.slice(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  if (luminance > 0.72) return 'light';
  if (luminance < 0.32) return 'dark';
  return 'mid';
}

function getBackground(step: any): AiAssetScreenContext['background'] {
  const background = step?.content?.canvas?.background;
  if (!background) return { screenKind: 'none' };
  if (background.kind === 'solid') {
    const screenColor = background.content?.color;
    return {
      screenKind: 'solid',
      screenColor,
      luminance: getLuminance(screenColor),
    };
  }
  if (background.kind === 'photo') return { screenKind: 'photo' };
  if (background.kind === 'clip') return { screenKind: 'clip' };
  return { screenKind: 'unknown' };
}

function formatBackgroundFitGuidance(background: AiAssetScreenContext['background'] | undefined): string | undefined {
  if (!background) return undefined;
  if (background.screenKind === 'solid' && background.luminance === 'dark') {
    return 'Match the dark screen background; use a dark, transparent-looking, or deep-toned backdrop and avoid a full white poster-style background unless explicitly requested.';
  }
  if (background.screenKind === 'solid' && background.luminance === 'light') {
    return 'Match the light screen background; keep the asset visually integrated and avoid a full black poster-style background unless explicitly requested.';
  }
  if (background.screenKind === 'solid') {
    return 'Match the screen background tone so the asset feels integrated, not like a separate poster.';
  }
  if (background.screenKind === 'photo' || background.screenKind === 'clip') {
    return 'Blend with the existing media background and avoid hard rectangular poster backdrops unless explicitly requested.';
  }
  return undefined;
}

function formatMeasuredVisualArea(position: AiAssetScreenContext['position'] | undefined): string | undefined {
  if (!position) return undefined;
  return `The measured asset area occupies ${position.widthPercentOfScreen}% of screen width and ${position.heightPercentOfScreen}% of screen height in the ${position.verticalBand}/${position.horizontalBand} region.`;
}

function getLayoutGuidance(
  screenType: string,
  slotKey: string,
  position?: AiAssetScreenContext['position'],
): AiAssetScreenContext['layout'] {
  const visualArea = formatMeasuredVisualArea(position);
  const optionSlot = /^(option|choice|answer)_\d+/.test(slotKey);
  const carouselSlot = /^carousel_\d+/.test(slotKey);

  switch (screenType) {
    case 'HeroContent':
      return {
        screenDescription: 'A mobile onboarding hero screen with one primary visual area, title/subtitle copy, and a bottom action button.',
        assetRoleDescription: 'The hero asset is the main visual metaphor for the screen copy, not a screenshot and not a full UI mockup.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Keep the lower copy/action area visually calm; avoid readable text inside the image and keep the subject away from cropped edges.',
      };
    case 'Carousel':
      return {
        screenDescription: 'A mobile onboarding carousel slide with a slide visual, supporting slide copy, and navigation/action controls.',
        assetRoleDescription: carouselSlot
          ? 'The asset belongs to one carousel slide and must feel consistent with sibling slide assets while still being understandable on its own.'
          : 'The asset supports the carousel slide narrative without replacing the slide copy.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Leave room for slide copy and controls; do not add readable labels, app screenshots, or extra claims.',
      };
    case 'HeroList':
      return {
        screenDescription: 'A mobile onboarding screen with a hero visual above or beside a compact list of benefits/options and a bottom action.',
        assetRoleDescription: 'The hero asset sets the visual tone while the list carries the detailed information.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Keep the visual simple enough that list rows remain the information focus.',
      };
    case 'ListSinglePick':
    case 'ListMultiPick':
      return {
        screenDescription: 'A question or selection screen with title/subtitle copy and vertically stacked answer rows.',
        assetRoleDescription: optionSlot
          ? 'The asset is a small answer-row visual cue; it must read clearly at thumbnail scale and should not compete with the option label.'
          : 'The asset is a supporting visual for the list question, not the main source of information.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Keep high contrast and simple shapes; avoid text, metrics, or busy scenes that reduce answer-row scanability.',
      };
    case 'GridSinglePick':
    case 'GridMultiPick':
      return {
        screenDescription: 'A selection screen with title/subtitle copy and a grid of selectable cards.',
        assetRoleDescription: optionSlot
          ? 'The asset is a compact card visual; it must remain legible in a small square or tile area.'
          : 'The asset supports the grid question while the cards/options carry the actual choices.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Favor simple centered objects and generous margins so the card grid stays scannable.',
      };
    case 'ProgressSteps':
      return {
        screenDescription: 'A progress or status onboarding screen with sequential steps, progress cues, and supporting copy.',
        assetRoleDescription: 'The asset should express progress or completion state visually without inventing exact metrics or UI labels.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Use abstract progress structure, not readable dashboards or fake statistics.',
      };
    case 'Paywall':
      return {
        screenDescription: 'A monetization screen with value messaging, benefit proof, plan/action controls, and optional media.',
        assetRoleDescription: 'The asset supports perceived value and trust while pricing and purchase actions remain outside the image.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Do not add prices, badges, guarantees, medical claims, or readable subscription UI.',
      };
    case 'FlexibleLayout':
      return {
        screenDescription: 'A custom composed onboarding layout where the measured slot geometry is the source of truth.',
        assetRoleDescription: 'The asset must fit the measured slot and support nearby copy without assuming a standard screen template.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Follow the measured container and leave safe edges for any adjacent or overlaid content.',
      };
    default:
      return {
        screenDescription: 'A mobile onboarding screen with copy, user controls, and one measured asset slot.',
        assetRoleDescription: 'The asset supports the screen narrative and must not become a separate UI screen or introduce new information.',
        visualAreaGuidance: visualArea,
        contentSafeArea: 'Respect the measured container, keep safe crop margins, and avoid readable text overlays.',
      };
  }
}

function getRendering(element: HTMLElement, root: HTMLElement): AiAssetScreenContext['rendering'] {
  const media = element.querySelector<HTMLElement>('img, video') ?? element;
  const style = window.getComputedStyle(media);
  const gradientOverlay = !!(
    element.querySelector('[data-testid="media-gradient"], [data-testid="paywall-media-gradient"]')
    ?? root.querySelector('[data-testid="media-gradient"], [data-testid="paywall-media-gradient"]')
  );
  const borderRadius = Number.parseFloat(style.borderRadius || '0');
  return {
    objectFit: style.objectFit || undefined,
    objectPosition: style.objectPosition || undefined,
    borderRadiusPx: Number.isFinite(borderRadius) ? borderRadius : undefined,
    gradientOverlay,
    behindTextOverlay: !!root.querySelector('[data-testid="paywall-media-gradient"]'),
  };
}

export function measureAiAssetScreenContext({
  previewRoot,
  step,
  slotKey,
  device,
}: {
  previewRoot: HTMLElement | null;
  step: any;
  slotKey: string;
  device?: string;
}): AiAssetScreenContext {
  const screenType = getScreenType(step);
  const unavailable = (warnings: string[]): AiAssetScreenContext => ({
    slotKey,
    screenType,
    device,
    source: 'unavailable',
    confidence: 'low',
    background: getBackground(step),
    layout: getLayoutGuidance(screenType, slotKey),
    warnings,
  });

  if (!previewRoot) return unavailable(['Preview root is not mounted.']);
  const root = getRootElement(previewRoot, screenType);
  if (!root) return unavailable([`Screen root for ${screenType} is not rendered.`]);

  const screenDomRect = root.getBoundingClientRect();
  if (!isVisibleRect(screenDomRect)) return unavailable(['Screen root has no measurable size.']);

  const target = getTargetElement(root, screenType, slotKey);
  if (!target.element) {
    return {
      ...unavailable(target.warnings),
      screenRect: toRectContext(screenDomRect),
      source: 'schema-fallback',
      warnings: [...target.warnings, 'Using real screenView size as fallback because the media container is not rendered.'],
    };
  }

  const slotDomRect = target.element.getBoundingClientRect();
  if (!isVisibleRect(slotDomRect)) {
    return {
      ...unavailable(['Slot container has no measurable size.', ...target.warnings]),
      screenRect: toRectContext(screenDomRect),
      source: 'schema-fallback',
      warnings: ['Slot container has no measurable size.', ...target.warnings, 'Using real screenView size as fallback.'],
    };
  }

  const imageElement = target.element.querySelector<HTMLElement>('img, video');
  const imageDomRect = imageElement?.getBoundingClientRect();
  const nearestPreset = findNearestAspectPreset(slotDomRect.width / slotDomRect.height);
  const aspect = slotDomRect.width / slotDomRect.height;
  const position = getPosition(slotDomRect, screenDomRect);

  return {
    slotKey,
    screenType,
    device,
    source: target.source,
    confidence: target.source === 'measured' ? 'high' : 'medium',
    screenRect: toRectContext(screenDomRect),
    slotRect: toRectContext(slotDomRect, screenDomRect),
    imageRect: imageDomRect && isVisibleRect(imageDomRect) ? toRectContext(imageDomRect, screenDomRect) : undefined,
    aspectRatio: {
      value: round(aspect, 3),
      label: ratioLabel(aspect),
      orientation: orientationForAspect(aspect),
      nearestPresetId: nearestPreset?.id,
      nearestPresetLabel: nearestPreset?.label,
    },
    position,
    rendering: getRendering(target.element, root),
    background: getBackground(step),
    layout: getLayoutGuidance(screenType, slotKey, position),
    warnings: target.warnings,
  };
}

export function formatScreenContextPromptBlock(context: AiAssetScreenContext): string {
  const lines = [
    SCREEN_CONTEXT_PROMPT_START,
    `Slot: ${context.slotKey}`,
    `Screen type: ${context.screenType}`,
    context.device ? `Preview device: ${context.device}` : undefined,
    `Measurement: ${context.source} (${context.confidence} confidence)`,
  ].filter(Boolean) as string[];

  if (context.slotRect) {
    lines.push(`Slot container: ${context.slotRect.width} x ${context.slotRect.height} CSS px`);
  }
  if (context.screenRect) {
    lines.push(`Screen container: ${context.screenRect.width} x ${context.screenRect.height} CSS px`);
  }
  if (context.aspectRatio) {
    lines.push(`Slot aspect ratio: ${context.aspectRatio.label} (${context.aspectRatio.orientation}, ${context.aspectRatio.value})`);
    if (context.aspectRatio.nearestPresetLabel) {
      lines.push(`Nearest generation preset: ${context.aspectRatio.nearestPresetLabel}`);
    }
  }
  if (context.position) {
    lines.push(`Slot position: ${context.position.verticalBand}/${context.position.horizontalBand}, ${context.position.widthPercentOfScreen}% screen width x ${context.position.heightPercentOfScreen}% screen height`);
  }
  if (context.background) {
    if (context.background.screenKind === 'solid' && context.background.screenColor) {
      lines.push(`Screen background: solid ${context.background.screenColor}${context.background.luminance ? ` (${context.background.luminance})` : ''}`);
    } else {
      lines.push(`Screen background: ${context.background.screenKind}`);
    }
    const backgroundFit = formatBackgroundFitGuidance(context.background);
    if (backgroundFit) {
      lines.push(`Background fit: ${backgroundFit}`);
    }
  }
  if (context.layout) {
    lines.push(`Screen layout: ${context.layout.screenDescription}`);
    lines.push(`Asset role: ${context.layout.assetRoleDescription}`);
    if (context.layout.visualAreaGuidance) {
      lines.push(`Visual area: ${context.layout.visualAreaGuidance}`);
    }
    if (context.layout.contentSafeArea) {
      lines.push(`Content safe area: ${context.layout.contentSafeArea}`);
    }
  }
  if (context.rendering?.objectFit || context.rendering?.objectPosition || context.rendering?.gradientOverlay) {
    const renderingBits = [
      context.rendering.objectFit ? `object-fit ${context.rendering.objectFit}` : undefined,
      context.rendering.objectPosition ? `object-position ${context.rendering.objectPosition}` : undefined,
      context.rendering.gradientOverlay ? 'gradient overlay present' : undefined,
      context.rendering.behindTextOverlay ? 'media may sit behind text overlays' : undefined,
    ].filter(Boolean);
    lines.push(`Rendering: ${renderingBits.join(', ')}`);
  }
  if (context.warnings?.length) {
    lines.push(`Warnings: ${context.warnings.join(' ')}`);
  }
  lines.push('Generation guidance: compose the asset for this exact container and keep key subject matter away from cropped edges or overlaid text.');
  lines.push(SCREEN_CONTEXT_PROMPT_END);

  return lines.join('\n');
}

export function injectScreenContextIntoPrompt(prompt: string | undefined, context: AiAssetScreenContext): string {
  const safePrompt = prompt ?? '';
  const block = formatScreenContextPromptBlock(context);
  const startIndex = safePrompt.indexOf(SCREEN_CONTEXT_PROMPT_START);
  const endIndex = safePrompt.indexOf(SCREEN_CONTEXT_PROMPT_END);

  if (startIndex >= 0 && endIndex > startIndex) {
    const before = safePrompt.slice(0, startIndex).trimEnd();
    const after = safePrompt.slice(endIndex + SCREEN_CONTEXT_PROMPT_END.length).trimStart();
    return [before, block, after].filter(Boolean).join('\n\n');
  }

  return [safePrompt.trimEnd(), block].filter(Boolean).join('\n\n');
}
